import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    Box, Button, Checkbox, Chip, CircularProgress, IconButton, Paper, Pagination, Stack, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import ConfirmDialog from "../components/ConfirmDialog";
import AdminJigsawTemplateService, { isJigsawTemplateInvisible } from "./AdminJigsawTemplateService";

const PAGE_SIZE = 15;

export default function AdminJigsawTemplateMain() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [savingEnabledId, setSavingEnabledId] = useState(null);
    const [savingInvisibleId, setSavingInvisibleId] = useState(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        const result = await AdminJigsawTemplateService.list(page - 1, PAGE_SIZE);
        setLoading(false);
        if (result.success) {
            setRows(result.data.content);
            setTotalPages(result.data.totalPages || 1);
        } else {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load" });
        }
    }, [page]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const handleCreateTemplate = () => {
        navigate("/admin/jigsaw/template/create");
    };

    const handleDelete = useCallback(async (id) => {
        const result = await AdminJigsawTemplateService.remove(id);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Delete failed" });
            return;
        }
        setSnackbar({ open: true, severity: "success", message: "Deleted" });
        fetchList();
    }, [fetchList]);

    const handleDeleteClick = (tpl) => {
        setDeleteTarget(tpl);
        setConfirmOpen(true);
    };

    const handleToggleEnabled = useCallback(async (tpl, checked) => {
        if (!tpl?.id) return;
        setSavingEnabledId(tpl.id);
        const result = await AdminJigsawTemplateService.setEnabled(tpl.id, checked);
        setSavingEnabledId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update enabled status" });
            return;
        }
        setRows((prev) =>
            prev.map((row) => (row.id === tpl.id ? { ...row, enabled: checked } : row)),
        );
        setSnackbar({
            open: true,
            severity: "success",
            message: checked ? "Template enabled" : "Template disabled",
        });
    }, []);

    const handleToggleInvisible = useCallback(async (tpl, checked) => {
        if (!tpl?.id) return;
        setSavingInvisibleId(tpl.id);
        const result = await AdminJigsawTemplateService.setInvisible(tpl.id, checked);
        setSavingInvisibleId(null);
        if (!result.success) {
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to update invisible status" });
            return;
        }
        setRows((prev) =>
            prev.map((row) => (row.id === tpl.id ? { ...row, invisible: checked } : row)),
        );
        setSnackbar({
            open: true,
            severity: "success",
            message: checked ? "Hidden from home jigsaw listing" : "Visible on home jigsaw listing",
        });
    }, []);

    const COL_SPAN = 8;

    return (
        <Box sx={{ p: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/jigsaw/browse")} sx={{ mb: 2 }}>
                Back to Jigsaw
            </Button>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5">Jigsaw Templates</Typography>
                <Button variant="contained" onClick={handleCreateTemplate}>
                    Create Template
                </Button>
            </Stack>

            <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 200 }}>Name</TableCell>
                                <TableCell sx={{ minWidth: 80 }}>Tile</TableCell>
                                <TableCell sx={{ minWidth: 200 }}>Detail</TableCell>
                                <TableCell sx={{ minWidth: 120 }}>Created</TableCell>
                                <TableCell sx={{ minWidth: 90 }}>Enabled</TableCell>
                                <TableCell sx={{ minWidth: 90 }} align="center">Invisible</TableCell>
                                <TableCell sx={{ minWidth: 88 }} align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow><TableCell colSpan={COL_SPAN} align="center" sx={{ py: 4 }}>No templates found.</TableCell></TableRow>
                            ) : (
                                rows.map((tpl) => {
                                    const isDisabled = tpl.enabled === false;
                                    return (
                                    <TableRow
                                        key={tpl.id}
                                        hover
                                        sx={isDisabled ? { bgcolor: "action.hover" } : undefined}
                                    >
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color={isDisabled ? "text.disabled" : "text.primary"}
                                                sx={{
                                                    fontWeight: isDisabled ? 400 : 600,
                                                    fontStyle: isDisabled ? "italic" : "normal",
                                                    cursor: "pointer",
                                                    ...(isDisabled
                                                        ? { opacity: 0.72 }
                                                        : { "&:hover": { textDecoration: "underline" } }),
                                                }}
                                                onClick={() => navigate(`/admin/jigsaw/template/edit/${tpl.id}`)}
                                            >
                                                {tpl.name || "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={`${tpl.tileSize}×${tpl.tileSize}`} variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                                {tpl.detail ? (JSON.stringify(tpl.detail)) : "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {tpl.createdAt ? new Date(tpl.createdAt).toLocaleDateString() : "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                size="small"
                                                checked={tpl.enabled !== false}
                                                disabled={savingEnabledId === tpl.id}
                                                onChange={(e) => void handleToggleEnabled(tpl, e.target.checked)}
                                                inputProps={{ "aria-label": `Enable ${tpl.name || tpl.id}` }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Hide from /home/jigsaw even when enabled">
                                                <Checkbox
                                                    size="small"
                                                    checked={isJigsawTemplateInvisible(tpl)}
                                                    disabled={savingInvisibleId === tpl.id}
                                                    onChange={(e) => void handleToggleInvisible(tpl, e.target.checked)}
                                                    inputProps={{ "aria-label": `Invisible ${tpl.name || tpl.id}` }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/admin/jigsaw/template/edit/${tpl.id}`)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => handleDeleteClick(tpl)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" showFirstButton showLastButton />
            </Stack>

            <CustomSnackbar open={snackbar.open} setOpen={(o) => setSnackbar((s) => ({ ...s, open: o }))} message={snackbar.message} severity={snackbar.severity} />

            <ConfirmDialog
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title="Delete Template"
                targetName={deleteTarget?.name || deleteTarget?.id || ""}
                targetId={deleteTarget?.id}
                message={[
                    "This template and all its related data will be permanently deleted.",
                    "This action cannot be undone."
                ]}
                warningAlert="Are you sure you want to proceed?"
                callback={(id) => handleDelete(id)}
            />
        </Box>
    );
}
